import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Store,
  Image,
  Moon,
  Sun,
  Palette,
  Save,
  Smartphone,
  Users,
  Receipt,
  FileBarChart,
  Settings,
  LogOut,
  ArrowLeft,
  LayoutDashboard,
  Bell,
  ShoppingCart,
  CreditCard,
  RotateCcw,
  Hash,
} from "lucide-react";

interface ShopSettings {
  shopName: string;
  shopAddress: string;
  phone1: string;
  phone2: string;
  receiptNote: string;
  logo: string;
  theme: "light" | "dark" | "system";
  primaryColor: string;
}

const SETTINGS_KEY = "qandil-settings";

const DEFAULT_SETTINGS: ShopSettings = {
  shopName: "پێشانگای قەندیل",
  shopAddress: "",
  phone1: "",
  phone2: "",
  receiptNote: "",
  logo: "",
  theme: "system",
  primaryColor: "default",
};

const COLORS = [
  { name: "بنەڕەت", value: "default", hslLight: "0 0% 9%", hslDark: "0 0% 98%" },
  { name: "شین", value: "blue", hslLight: "221.2 83.2% 53.3%", hslDark: "217.2 91.2% 59.8%" },
  { name: "سەوز", value: "green", hslLight: "142.1 76.2% 36.3%", hslDark: "142.1 70.6% 45.3%" },
  { name: "سوور", value: "red", hslLight: "0 72.2% 50.6%", hslDark: "0 72.2% 50.6%" },
  { name: "مۆر", value: "purple", hslLight: "262.1 83.3% 57.8%", hslDark: "263.4 70% 50.4%" },
  { name: "نارنجی", value: "orange", hslLight: "24.6 95% 53.1%", hslDark: "20.5 90.2% 48.2%" },
];

interface SettingsPageProps {
  open: boolean;
  onClose: () => void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports") => void;
}

export function SettingsPage({ open, onClose, onNavigate }: SettingsPageProps) {
  const [settings, setSettings] = useState<ShopSettings>(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [isResettingInvoice, setIsResettingInvoice] = useState(false);
  const [invoiceResetSuccess, setInvoiceResetSuccess] = useState(false);

  const getApiUrl = () => {
    return import.meta.env.VITE_KV_API_URL || 'https://qandil-realtime-api.qandil.workers.dev';
  };

  const resetInvoiceNumber = async () => {
    if (!confirm("دڵنیایت لە سفرکردنەوەی ژمارەی وەصڵ؟ ئەم کارە ناگەڕێتەوە.")) return;
    
    setIsResettingInvoice(true);
    try {
      const token = localStorage.getItem('auth_token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/invoice-number/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        setInvoiceResetSuccess(true);
        setTimeout(() => setInvoiceResetSuccess(false), 3000);
      } else {
        alert('هەڵە لە سفرکردنەوەی ژمارەی وەصڵ');
      }
    } catch (error) {
      console.error('Error resetting invoice number:', error);
      alert('هەڵە لە سفرکردنەوەی ژمارەی وەصڵ');
    } finally {
      setIsResettingInvoice(false);
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      setSettings(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const isDark = settings.theme === "dark" || 
      (settings.theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    
    if (settings.theme === "dark") {
      root.classList.add("dark");
    } else if (settings.theme === "light") {
      root.classList.remove("dark");
    } else {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    }

    const selectedColor = COLORS.find((c) => c.value === settings.primaryColor);
    if (selectedColor) {
      // لە دارک مۆد: ڕەنگی سەرەکی ڕووناکتر، لە لایت مۆد: تاریکتر
      const hsl = isDark ? selectedColor.hslDark : selectedColor.hslLight;
      root.style.setProperty("--primary", `hsl(${hsl})`);
      // ڕەنگی نوسین بە پێچەوانەی ڕەنگی سەرەکی
      if (isDark) {
        root.style.setProperty("--primary-foreground", "hsl(0 0% 9%)");
      } else {
        root.style.setProperty("--primary-foreground", "hsl(0 0% 98%)");
      }
    }
  }, [settings.theme, settings.primaryColor]);

  const handleSave = () => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({ ...settings, logo: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col md:flex-row" dir="rtl">
      {/* Mobile Header */}
      <header className="md:hidden border-b bg-background z-10">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Settings className="h-4 w-4 text-primary" />
            </div>
            <span className="font-semibold text-sm">ڕێکخستن</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSave} disabled={saved} className="h-8 text-xs">
              {saved ? "پاشەکەوتکرا!" : "پاشەکەوت"}
              <Save className="h-3 w-3 mr-1" />
            </Button>
            <Button variant="outline" size="sm" onClick={onClose} className="h-8">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-56 border-l bg-white dark:bg-muted/30 flex-col h-screen">
        <div className="flex items-center gap-3 h-16 px-4 border-b">
          <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center shadow-md">
            <span className="text-primary-foreground font-bold text-xl">ق</span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-sm">پێشانگای قەندیل</span>
            <span className="text-xs text-muted-foreground">سیستەمی بەڕێوەبردن</span>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <button
            onClick={() => { onClose(); onNavigate?.("dashboard"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer"
          >
            <LayoutDashboard className="h-4 w-4" />
            داشبۆرد
          </button>
          <button
            onClick={() => { onClose(); onNavigate?.("customers"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer"
          >
            <Users className="h-4 w-4" />
            کڕیارەکان
          </button>
          <button
            onClick={() => { onClose(); onNavigate?.("sales"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer"
          >
            <ShoppingCart className="h-4 w-4" />
            فرۆشتنەکان
          </button>
          <button
            onClick={() => { onClose(); onNavigate?.("payments"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer"
          >
            <CreditCard className="h-4 w-4" />
            پارەدان
          </button>
          <button
            onClick={() => { onClose(); onNavigate?.("expenses"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer"
          >
            <Receipt className="h-4 w-4" />
            خەرجییەکان
          </button>
          <button
            onClick={() => { onClose(); onNavigate?.("notifications"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer"
          >
            <Bell className="h-4 w-4" />
            دواکەوتووەکان
          </button>
          <button
            onClick={() => { onClose(); onNavigate?.("reports"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer"
          >
            <FileBarChart className="h-4 w-4" />
            ڕاپۆرتەکان
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-primary/10 text-primary cursor-pointer">
            <Settings className="h-4 w-4" />
            ڕێکخستن
          </button>
        </nav>
        <div className="p-3 border-t">
          <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted cursor-pointer">
            <LogOut className="h-4 w-4" />
            چوونەدەرەوە
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Desktop Header */}
        <header className="hidden md:block border-b sticky top-0 z-50 bg-background">
          <div className="flex items-center justify-between h-16 px-6">
            <span className="font-semibold truncate">ڕێکخستن</span>
            <div className="flex items-center gap-2">
              <Button onClick={handleSave} disabled={saved}>
                {saved ? "پاشەکەوتکرا!" : "پاشەکەوتکردن"}
                <Save className="h-4 w-4 mr-2" />
              </Button>
              <Button variant="outline" onClick={onClose}>
                گەڕانەوە
                <ArrowLeft className="h-4 w-4 mr-2" />
              </Button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-3 md:p-6 space-y-3 md:space-y-4">
          {/* Shop Info & Logo Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                زانیاری پێشانگا
              </CardTitle>
              <CardDescription>زانیاری سەرەکی پێشانگا و لۆگۆ</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                {/* Logo */}
                <div className="flex flex-col items-center gap-2 md:items-start">
                  <div className="h-20 w-20 md:h-24 md:w-24 rounded-lg border-2 border-dashed flex items-center justify-center bg-muted/30 overflow-hidden">
                    {settings.logo ? (
                      <img src={settings.logo} alt="Logo" className="h-full w-full object-contain" />
                    ) : (
                      <Image className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="logo-upload" className="cursor-pointer">
                      <Button variant="outline" size="sm" asChild>
                        <span>
                          هەڵبژاردنی وێنە
                          <Image className="h-4 w-4 mr-2" />
                        </span>
                      </Button>
                    </Label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    {settings.logo && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSettings({ ...settings, logo: "" })}
                        className="text-destructive h-8"
                      >
                        سڕینەوە
                      </Button>
                    )}
                  </div>
                </div>
                {/* Info Fields */}
                <div className="flex-1 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>ناوی پێشانگا</Label>
                      <Input
                        value={settings.shopName}
                        onChange={(e) => setSettings({ ...settings, shopName: e.target.value })}
                        placeholder="ناوی پێشانگا"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ناونیشانی پێشانگا</Label>
                      <Input
                        value={settings.shopAddress}
                        onChange={(e) => setSettings({ ...settings, shopAddress: e.target.value })}
                        placeholder="ناونیشانی پێشانگا"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ژمارە مۆبایل ١</Label>
                      <Input
                        value={settings.phone1}
                        onChange={(e) => setSettings({ ...settings, phone1: e.target.value })}
                        placeholder="07xxxxxxxxx"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>ژمارە مۆبایل ٢</Label>
                      <Input
                        value={settings.phone2}
                        onChange={(e) => setSettings({ ...settings, phone2: e.target.value })}
                        placeholder="07xxxxxxxxx"
                        dir="ltr"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>تێبینی وەصڵ</Label>
                    <Textarea
                      value={settings.receiptNote}
                      onChange={(e) => setSettings({ ...settings, receiptNote: e.target.value })}
                      placeholder="ئەم تێبینییە لە خوارەوەی وەصڵەکان دەردەکەوێت..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Theme Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                شێواز
              </CardTitle>
              <CardDescription>شێوازی ڕووکار و ڕەنگ</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Theme Mode */}
                <div className="space-y-3">
                  <Label>دۆخی ڕووکار</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant={settings.theme === "light" ? "default" : "outline"}
                      onClick={() => setSettings({ ...settings, theme: "light" })}
                      className="flex-1"
                    >
                      ڕووناک
                      <Sun className="h-4 w-4 mr-2" />
                    </Button>
                    <Button
                      variant={settings.theme === "dark" ? "default" : "outline"}
                      onClick={() => setSettings({ ...settings, theme: "dark" })}
                      className="flex-1"
                    >
                      تاریک
                      <Moon className="h-4 w-4 mr-2" />
                    </Button>
                    <Button
                      variant={settings.theme === "system" ? "default" : "outline"}
                      onClick={() => setSettings({ ...settings, theme: "system" })}
                      className="flex-1"
                    >
                      سیستەم
                      <Smartphone className="h-4 w-4 mr-2" />
                    </Button>
                  </div>
                </div>

                {/* Primary Color */}
                <div className="space-y-3">
                  <Label>ڕەنگی سەرەکی</Label>
                  <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-6 lg:grid-cols-8 gap-2 md:gap-3">
                    {COLORS.map((color) => {
                      const isDark = settings.theme === "dark" || 
                        (settings.theme === "system" && typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches);
                      const hsl = isDark ? color.hslDark : color.hslLight;
                      return (
                      <button
                        key={color.value}
                        onClick={() => setSettings({ ...settings, primaryColor: color.value })}
                        className={`h-8 w-8 md:h-10 md:w-10 rounded-full border-2 cursor-pointer transition-all hover:scale-110 ${
                          settings.primaryColor === color.value
                            ? "ring-2 ring-offset-2 ring-foreground scale-110"
                            : "border-border"
                        }`}
                        style={{ backgroundColor: `hsl(${hsl})` }}
                        title={color.name}
                      />
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Number Reset Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5" />
                ژمارەی وەصڵ
              </CardTitle>
              <CardDescription>بەڕێوەبردنی ژمارەی وەصڵی پارەدان</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">
                    ئەگەر دەتەوێت ژمارەی وەصڵ لە سەرەوە دەست پێ بکاتەوە، ئەم دوگمەیە بکلیک بکە.
                    ئەم کارە ناگەڕێتەوە و ژمارەی وەصڵ دەبێتە ١ بۆ پارەدانی داهاتوو.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  onClick={resetInvoiceNumber}
                  disabled={isResettingInvoice}
                  className="shrink-0"
                >
                  {isResettingInvoice ? (
                    "چاوەڕوان بە..."
                  ) : invoiceResetSuccess ? (
                    "سفر کرایەوە!"
                  ) : (
                    <>
                      سفرکردنەوەی ژمارەی وەصڵ
                      <RotateCcw className="h-4 w-4 mr-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
